/**
 * Auth API service - handles authentication API calls
 */

import { apiClient } from './client';

// Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface RegisterRequest {
  email: string;
  password: string;
  displayName?: string;
  locale?: string;
}

export interface RegisterResponse {
  user: User;
  message: string;
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  locale: string;
  theme: 'light' | 'dark' | 'system';
  timezone: string;
  isEmailVerified: boolean;
  isSuperAdmin: boolean;
  twoFactorEnabled?: boolean;
  notificationSettings?: NotificationSettings;
  createdAt: string;
}

export interface NotificationSettings {
  email: {
    taskAssigned: boolean;
    taskUpdated: boolean;
    taskCompleted: boolean;
    taskComment: boolean;
    taskMention: boolean;
    dueDateReminder: boolean;
    weeklyDigest: boolean;
  };
  push: {
    taskAssigned: boolean;
    taskUpdated: boolean;
    taskComment: boolean;
    taskMention: boolean;
    dueDateReminder: boolean;
  };
  inApp: {
    taskAssigned: boolean;
    taskUpdated: boolean;
    taskCompleted: boolean;
    taskComment: boolean;
    taskMention: boolean;
    dueDateReminder: boolean;
  };
}

export interface UpdateProfileRequest {
  displayName?: string;
  avatarUrl?: string;
  locale?: string;
  timezone?: string;
  theme?: 'light' | 'dark' | 'system';
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface TwoFactorSetupResponse {
  qrCode: string;
  secret: string;
}

export interface TwoFactorVerifyRequest {
  code: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface MagicLinkRequest {
  email: string;
}

export interface OAuthInitResponse {
  authorizationUrl: string;
  state: string;
}

export interface OAuthCallbackRequest {
  code: string;
  state: string;
}

export interface RegistrationStatusResponse {
  enabled: boolean;
  message: string | null;
}

/**
 * Auth API methods
 */
export const authApi = {
  /**
   * Check if registration is enabled
   */
  async getRegistrationStatus(): Promise<RegistrationStatusResponse> {
    const response = await apiClient.get<any>('/auth/registration-status');
    return {
      enabled: response.data.enabled,
      message: response.data.message,
    };
  },

  /**
   * Register a new user account
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await apiClient.post<RegisterResponse>('/auth/register', {
      email: data.email,
      password: data.password,
      display_name: data.displayName,
      locale: data.locale || 'en',
    });
    return {
      user: transformUser(response.data.user),
      message: response.data.message,
    };
  },

  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<any>('/auth/login', data);
    return {
      accessToken: response.data.accessToken ?? response.data.access_token,
      refreshToken: response.data.refreshToken ?? response.data.refresh_token,
      expiresIn: response.data.expiresIn ?? response.data.expires_in,
      user: transformUser(response.data.user),
    };
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<any>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return {
      accessToken: response.data.accessToken ?? response.data.access_token,
      refreshToken: response.data.refreshToken ?? response.data.refresh_token,
      expiresIn: response.data.expiresIn ?? response.data.expires_in,
    };
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<any>('/auth/me');
    return transformUser(response.data);
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/reset-password', { token, password });
  },

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<void> {
    await apiClient.post('/auth/verify-email', { token });
  },

  /**
   * Request magic link login
   */
  async requestMagicLink(email: string): Promise<void> {
    await apiClient.post('/auth/magic-link', { email });
  },

  /**
   * Login with magic link token
   */
  async loginWithMagicLink(token: string): Promise<LoginResponse> {
    const response = await apiClient.post<any>('/auth/magic-link/verify', { token });
    return {
      accessToken: response.data.accessToken ?? response.data.access_token,
      refreshToken: response.data.refreshToken ?? response.data.refresh_token,
      expiresIn: response.data.expiresIn ?? response.data.expires_in,
      user: transformUser(response.data.user),
    };
  },

  /**
   * Get OAuth authorization URL
   */
  async getOAuthUrl(provider: 'google' | 'microsoft'): Promise<OAuthInitResponse> {
    const response = await apiClient.get<any>(`/auth/oauth/${provider}/authorize`);
    return {
      authorizationUrl: response.data.authorizationUrl ?? response.data.authorization_url,
      state: response.data.state,
    };
  },

  /**
   * Complete OAuth login
   */
  async oauthCallback(
    provider: 'google' | 'microsoft',
    code: string,
    state: string
  ): Promise<LoginResponse> {
    const response = await apiClient.post<any>(`/auth/oauth/${provider}/callback`, {
      code,
      state,
    });
    return {
      accessToken: response.data.accessToken ?? response.data.access_token,
      refreshToken: response.data.refreshToken ?? response.data.refresh_token,
      expiresIn: response.data.expiresIn ?? response.data.expires_in,
      user: transformUser(response.data.user),
    };
  },

  /**
   * Resend verification email
   */
  async resendVerification(): Promise<void> {
    await apiClient.post('/auth/resend-verification');
  },

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await apiClient.patch<any>('/auth/profile', {
      display_name: data.displayName,
      avatar_url: data.avatarUrl,
      locale: data.locale,
      timezone: data.timezone,
      theme: data.theme,
    });
    return transformUser(response.data);
  },

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await apiClient.post('/auth/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword,
    });
  },

  /**
   * Enable two-factor authentication
   */
  async enable2FA(): Promise<TwoFactorSetupResponse> {
    const response = await apiClient.post<any>('/auth/2fa/enable');
    return {
      qrCode: response.data.qrCode ?? response.data.qr_code,
      secret: response.data.secret,
    };
  },

  /**
   * Verify and complete 2FA setup
   */
  async verify2FA(data: TwoFactorVerifyRequest): Promise<void> {
    await apiClient.post('/auth/2fa/verify', { code: data.code });
  },

  /**
   * Disable two-factor authentication
   */
  async disable2FA(): Promise<void> {
    await apiClient.post('/auth/2fa/disable');
  },

  /**
   * Update notification settings
   */
  async updateNotificationSettings(settings: NotificationSettings): Promise<void> {
    await apiClient.patch('/auth/notifications', settings);
  },

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<void> {
    await apiClient.delete('/auth/account');
  },
};

/**
 * Transform API response to camelCase
 * Note: Backend uses alias_generator=to_camel, so API responses are camelCase
 */
function transformUser(data: any): User {
  return {
    id: data.id,
    email: data.email,
    displayName: data.displayName ?? data.display_name,
    avatarUrl: data.avatarUrl ?? data.avatar_url,
    locale: data.locale,
    theme: data.theme,
    timezone: data.timezone,
    isEmailVerified: data.isEmailVerified ?? data.is_email_verified,
    isSuperAdmin: data.isSuperAdmin ?? data.is_super_admin ?? false,
    createdAt: data.createdAt ?? data.created_at,
  };
}

export default authApi;
